import { useEffect, useState } from "react";
import { ArrowsClockwise } from "@phosphor-icons/react";
import api from "../lib/api";
import { fmt$ } from "./scm/shared";
import { Products } from "./scm/Products";
import { Suppliers } from "./scm/Suppliers";
import { Orders, PO_CFG, SO_CFG } from "./scm/Orders";
import { StockAdjustments } from "./scm/StockAdjustments";
import { Costing } from "./scm/Costing";
import { Locations } from "./scm/Locations";
import { StockLedger } from "./scm/StockLedger";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Products", "Suppliers", "Purchase Orders", "Sales Orders", "Locations", "Stock Adjustments", "Stock Ledger", "Costing", "Reports"];

export default function SCMPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Products");
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [sos, setSos] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tick, setTick] = useState(0);

  const refresh = async () => {
    const [s, p, sup, po, so, loc] = await Promise.all([
      api.get("/scm/summary").then((r) => r.data),
      api.get("/scm/products").then((r) => r.data),
      api.get("/scm/suppliers").then((r) => r.data),
      api.get("/scm/purchase-orders").then((r) => r.data),
      api.get("/scm/sales-orders").then((r) => r.data),
      api.get("/scm/locations").then((r) => r.data),
    ]);
    setSummary(s); setProducts(p); setSuppliers(sup); setPos(po); setSos(so); setLocations(loc);
    setTick((t) => t + 1);
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="scm-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 03 · SUPPLY CHAIN</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Supply Chain.</h1>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1" data-testid="scm-refresh-btn">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-zinc-200 mb-6" data-testid="scm-summary">
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">SKUs</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.product_count}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Inventory Value (avg cost)</div>
            <div className="font-display font-black text-2xl tnum mt-1">{fmt$(summary.inventory_value)}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Low Stock</div>
            <div className="font-display font-black text-2xl tnum mt-1 text-[#FF3E00]">{summary.low_stock_count}</div>
          </div>
          <div className="px-6 py-4">
            <div className="label-sm text-zinc-500">Open Orders</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.po_open + summary.so_open}</div>
          </div>
        </div>
      )}

      <div className="flex border-b border-zinc-950 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`scm-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950">
        {tab === "Products" && <Products products={products} onChange={refresh} />}
        {tab === "Suppliers" && <Suppliers suppliers={suppliers} onChange={refresh} />}
        {tab === "Purchase Orders" && <Orders cfg={PO_CFG} orders={pos} suppliers={suppliers} products={products} locations={locations} refreshKey={tick} onChange={refresh} />}
        {tab === "Sales Orders" && <Orders cfg={SO_CFG} orders={sos} suppliers={suppliers} products={products} locations={locations} refreshKey={tick} onChange={refresh} />}
        {tab === "Locations" && <Locations products={products} locations={locations} refreshKey={tick} onChange={refresh} />}
        {tab === "Stock Adjustments" && <StockAdjustments products={products} locations={locations} refreshKey={tick} onChange={refresh} />}
        {tab === "Stock Ledger" && <StockLedger products={products} locations={locations} refreshKey={tick} />}
        {tab === "Costing" && <Costing refreshKey={tick} />}
        {tab === "Reports" && <StandardReports module="scm" />}
      </div>
    </div>
  );
}
